import { navigate } from 'gatsby'
import React, { useEffect } from 'react'

import { Locale } from '../context/LocaleContextProvider'
import { frenchRedirectScript, getFrenchRedirect, readLanguagePreference } from '../helpers/languagePreference'

// Set by gatsby-browser's onInitialClientRender. Until then React is hydrating the static HTML, which the inline
// script below has already handled; rendering anything different then would break hydration. `gatsby develop`
// (local and the hosted preview) renders on the client without static HTML, so the gate handles the first load.
let hasHydrated = process.env.NODE_ENV === 'development'
export const markHydrated = () => {
  hasHydrated = true
}

interface IProps {
  locale: Locale
  alternates: Partial<Record<Locale, string>>
}

/**
 * @description sends visitors who picked French with the language toggle to the French version of an English page.
 * - First load: an inline script at the top of the page redirects before anything renders.
 * - Client-side navigation: the page isn't rendered (so no English Page Viewed is sent) and we navigate instead.
 * Pages without a French version, French pages and crawlers (no cookie) render as usual.
 */
export const LanguagePreferenceGate: React.FC<IProps> = ({ locale, alternates, children }) => {
  const frenchPath = getFrenchRedirect({ preference: 'fr-CA', locale, alternates })
  const redirectTo =
    hasHydrated && typeof window !== 'undefined' && window.self === window.top
      ? getFrenchRedirect({ preference: readLanguagePreference(), locale, alternates })
      : null

  useEffect(() => {
    if (redirectTo) navigate(`${redirectTo}${window.location.search}${window.location.hash}`, { replace: true })
  }, [redirectTo])

  if (redirectTo) return null

  return (
    <>
      {frenchPath && <script dangerouslySetInnerHTML={{ __html: frenchRedirectScript(frenchPath) }} />}
      {children}
    </>
  )
}
